import React, { useState } from 'react';
import { SearchIcon, ChevronRightIcon, TrashIcon, HomeIcon, BuildingIcon, TruckIcon, ClipboardIcon } from 'lucide-react';
import { CategorySection } from '../components/CategorySection';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
const services: Service[] = [
// Residential Services
{
  id: 'rs1',
  title: 'Residential Waste Collection Registration',
  description: 'Register for regular household waste collection service',
  category: 'Residential Services',
  estimatedTime: '2-3 business days',
  fee: 'Varies by bin size'
}, {
  id: 'rs2',
  title: 'Residential Recycling Registration',
  description: 'Sign up for household recycling collection service',
  category: 'Residential Services',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
}, {
  id: 'rs3',
  title: 'Organic Waste Collection Registration',
  description: 'Register for green bin organic waste collection',
  category: 'Residential Services',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
}, {
  id: 'rs4',
  title: 'Waste Bin Replacement Request',
  description: 'Request replacement for damaged or missing waste bins',
  category: 'Residential Services',
  estimatedTime: '3-5 business days',
  fee: '$25.00 if damaged by resident'
},
// Commercial Services
{
  id: 'cs1',
  title: 'Commercial Waste Collection Registration',
  description: 'Register business for regular waste collection service',
  category: 'Commercial Services',
  estimatedTime: '3-5 business days',
  fee: 'Based on volume'
}, {
  id: 'cs2',
  title: 'Commercial Collection Frequency Update',
  description: 'Modify collection schedule or bin capacity',
  category: 'Commercial Services',
  estimatedTime: '2-3 business days',
  fee: '$50.00'
}, {
  id: 'cs3',
  title: 'Commercial Recycling Program',
  description: 'Register for business recycling collection service',
  category: 'Commercial Services',
  estimatedTime: '3-5 business days',
  fee: 'Based on volume'
}, {
  id: 'cs4',
  title: 'Commercial Composting Service',
  description: 'Organic waste collection for businesses',
  category: 'Commercial Services',
  estimatedTime: '3-5 business days',
  fee: 'Based on volume'
},
// Special Collections
{
  id: 'sc1',
  title: 'Bulk Waste Pickup Scheduling',
  description: 'Schedule collection of large items or excess waste',
  category: 'Special Collections',
  estimatedTime: '5-7 business days',
  fee: '$75.00 per pickup'
}, {
  id: 'sc2',
  title: 'Hazardous Waste Disposal',
  description: 'Schedule drop-off of hazardous household materials',
  category: 'Special Collections',
  estimatedTime: '1-2 business days',
  fee: 'No fee for households'
}, {
  id: 'sc3',
  title: 'Electronic Waste Collection',
  description: 'Schedule pickup of electronic waste items',
  category: 'Special Collections',
  estimatedTime: '3-5 business days',
  fee: '$25.00 per item'
}, {
  id: 'sc4',
  title: 'Construction Waste Removal',
  description: 'Schedule removal of construction or renovation debris',
  category: 'Special Collections',
  estimatedTime: '2-3 business days',
  fee: 'Based on volume'
},
// Service Support & Reporting
{
  id: 'sr1',
  title: 'Missed Collection Report',
  description: 'Report a missed waste or recycling collection',
  category: 'Service Support & Reporting',
  estimatedTime: '1 business day',
  fee: 'No fee'
}, {
  id: 'sr2',
  title: 'Service Issue Resolution',
  description: 'Report ongoing collection issues or concerns',
  category: 'Service Support & Reporting',
  estimatedTime: '2-3 business days',
  fee: 'No fee'
}, {
  id: 'sr3',
  title: 'Collection Schedule Inquiry',
  description: 'View or request waste collection schedule',
  category: 'Service Support & Reporting',
  estimatedTime: 'Same day',
  fee: 'No fee'
}, {
  id: 'sr4',
  title: 'Illegal Dumping Report',
  description: 'Report illegal waste disposal in your area',
  category: 'Service Support & Reporting',
  estimatedTime: '1-2 business days',
  fee: 'No fee'
}];
const CategoryIcon = ({
  category
}: {
  category: string;
}) => {
  switch (category) {
    case 'Residential Services':
      return <HomeIcon className="h-6 w-6 text-blue-600" data-id="element-5452" />;
    case 'Commercial Services':
      return <BuildingIcon className="h-6 w-6 text-green-600" data-id="element-5453" />;
    case 'Special Collections':
      return <TruckIcon className="h-6 w-6 text-purple-600" data-id="element-5454" />;
    case 'Service Support & Reporting':
      return <ClipboardIcon className="h-6 w-6 text-orange-600" data-id="element-5455" />;
    default:
      return <TrashIcon className="h-6 w-6 text-gray-600" data-id="element-5456" />;
  }
};
export const WasteManagementPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = services.filter(service => service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase()));
  const categories = Array.from(new Set(filteredServices.map(service => service.category)));
  return <div className="min-h-screen bg-gray-50" data-id="element-5457">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5458">
        {/* Breadcrumb */}
        <div className="flex items-center text-sm text-gray-500 mb-8" data-id="element-5459">
          <a href="/" className="hover:text-gray-700" data-id="element-5460">
            Dashboard
          </a>
          <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5461" />
          <span className="text-gray-900" data-id="element-5462">Waste Management Services</span>
        </div>
        {/* Header */}
        <div className="mb-8" data-id="element-5463">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-5464">
            Waste Management Services
          </h1>
          <p className="text-gray-600" data-id="element-5465">
            Register for waste collection services and manage your waste
            disposal needs
          </p>
        </div>
        {/* Search */}
        <div className="mb-8" data-id="element-5466">
          <div className="max-w-md relative" data-id="element-5467">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-5468">
              <SearchIcon className="h-5 w-5" data-id="element-5469" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-5470" />
          </div>
        </div>
        {/* Service Categories */}
        {categories.map(category => <CategorySection key={category} category={category} services={filteredServices.filter(service => service.category === category)} icon={<CategoryIcon category={category} data-id="element-5472" />} buttonText="Request Service" data-id="element-5471" />)}
      </div>
    </div>;
};